import { verificationAPI } from "./verificationAPI";

export const VERIFICATION_CONSENT_POLICY_VERSION = "enterprise-post-triage-kyb-v3";
export const VERIFICATION_POLL_INTERVAL_MS = 3000;
export const VERIFICATION_POLL_MAX_INTERVAL_MS = 30000;
export const VERIFICATION_TERMINAL_POLL_INTERVAL_MS = 15000;
export const VERIFICATION_FAST_POLL_WINDOW_MS = 90 * 1000;
export const VERIFICATION_PREPARATION_TIMEOUT_MS = 90 * 1000;
export const VERIFICATION_TOKEN_STORAGE_KEY = "dakasa:enterprise-verification-token";
export const ACCESS_REQUEST_SUBMISSION_STORAGE_KEY = "dakasa:enterprise-access-request-submission";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const verificationStatuses = [
  "invited",
  "starting",
  "in_progress",
  "approved",
  "declined",
  "manual_review",
  "provider_error",
  "expired",
] as const;

const authorityStatuses = ["pending", "verified", "manual_review", "rejected"] as const;
const companyVerificationStatuses = [
  "pending",
  "verified",
  "mismatch",
  "manual_review",
  "failed",
] as const;
const identityVerificationStatuses = ["pending", "verified", "manual_review", "failed"] as const;
const authorityEvidenceStatuses = ["pending", "verified", "failed", "not_required"] as const;
const reviewScopes = [
  "none",
  "authority",
  "authority_evidence",
  "company",
  "identity",
  "compliance",
  "ownership_conflict",
] as const;
const verificationOutcomeClasses = [
  "pending",
  "approved",
  "hard_declined",
  "retryable_not_approved",
] as const;

export type VerificationStatus = (typeof verificationStatuses)[number];
export type AuthorityStatus = (typeof authorityStatuses)[number];
export type CompanyVerificationStatus = (typeof companyVerificationStatuses)[number];
export type IdentityVerificationStatus = (typeof identityVerificationStatuses)[number];
export type AuthorityEvidenceStatus = (typeof authorityEvidenceStatuses)[number];
export type VerificationReviewScope = (typeof reviewScopes)[number];
export type VerificationOutcomeClass = (typeof verificationOutcomeClasses)[number];

export interface VerificationConsentPolicy {
  version: string;
  notice: string;
  provider: string;
  daKasaPrivacyUrl: string;
  providerPrivacyUrl: string;
  providerTermsUrl: string;
  providerRetentionDays: number;
}

export interface AccessRequestIntake {
  companyName: string;
  cnpj: string;
  companyEmail: string;
  companyPhone: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  street: string;
  numberStreet: string;
  neighborhood: string;
  applicantName: string;
  applicantEmail: string;
  applicantPhone: string;
}

export interface AccessRequestPayload {
  submissionId: string;
  companyName: string;
  cnpj: string;
  email: string;
  phone: string;
  phoneCountry: string;
  phoneArea: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  street: string;
  numberStreet: string;
  neighborhood: string;
  applicantName: string;
  applicantEmail: string;
  applicantPhone: string;
}

export interface AccessRequestSubmissionResponse {
  status: "submitted";
  cnpj: string;
  registryStatus: string;
}

export interface OwnerActivationSummary {
  orgId: string;
  inviteId: string;
}

export interface OwnerActivation extends OwnerActivationSummary {
  inviteToken: string;
  inviteUrl: string;
}

export interface VerificationSessionView {
  verificationId: string;
  accessRequestId: string;
  verificationStatus: VerificationStatus;
  authorityStatus: AuthorityStatus;
  companyVerificationStatus: CompanyVerificationStatus;
  identityVerificationStatus: IdentityVerificationStatus;
  authorityEvidenceStatus: AuthorityEvidenceStatus;
  reviewScope: VerificationReviewScope;
  outcomeClass: VerificationOutcomeClass;
  outcomeSettled: boolean;
  settlementDeadlineAt?: string;
  provider: string;
  providerStatus: string;
  providerUrl?: string;
  reasonCode?: string;
  expiresAt: string;
  activationClaimed: boolean;
  activation?: OwnerActivationSummary;
  consentPolicy: VerificationConsentPolicy;
}

export function verificationPollDelay(
  consecutiveFailures: number,
  terminalObservation = false,
  random: () => number = Math.random,
): number {
  const failures = Math.max(0, Math.min(4, Math.trunc(consecutiveFailures)));
  const base = terminalObservation
    ? VERIFICATION_TERMINAL_POLL_INTERVAL_MS
    : Math.min(VERIFICATION_POLL_MAX_INTERVAL_MS, VERIFICATION_POLL_INTERVAL_MS * 2 ** failures);
  const jitter = Math.max(0, Math.min(1, random()));
  return Math.round(Math.min(VERIFICATION_POLL_MAX_INTERVAL_MS, base * (1 + jitter * 0.2)));
}

function isVerificationStatus(value: unknown): value is VerificationStatus {
  return verificationStatuses.includes(value as VerificationStatus);
}

function isAuthorityStatus(value: unknown): value is AuthorityStatus {
  return authorityStatuses.includes(value as AuthorityStatus);
}

function isCompanyVerificationStatus(value: unknown): value is CompanyVerificationStatus {
  return companyVerificationStatuses.includes(value as CompanyVerificationStatus);
}

function isIdentityVerificationStatus(value: unknown): value is IdentityVerificationStatus {
  return identityVerificationStatuses.includes(value as IdentityVerificationStatus);
}

function isAuthorityEvidenceStatus(value: unknown): value is AuthorityEvidenceStatus {
  return authorityEvidenceStatuses.includes(value as AuthorityEvidenceStatus);
}

function isReviewScope(value: unknown): value is VerificationReviewScope {
  return reviewScopes.includes(value as VerificationReviewScope);
}

function isVerificationOutcomeClass(value: unknown): value is VerificationOutcomeClass {
  return verificationOutcomeClasses.includes(value as VerificationOutcomeClass);
}

function requiredString(value: unknown, field: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`Invalid verification response: ${field}`);
  }
  return value;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function nonNegativeInteger(value: unknown, field: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw new Error(`Invalid verification response: ${field}`);
  }
  return value;
}

function createBrowserUUID(): string {
  if (typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
}

export function resolveAccessRequestSubmissionId(
  storage: Storage,
  createId: () => string = createBrowserUUID,
): string {
  try {
    const existing = storage.getItem(ACCESS_REQUEST_SUBMISSION_STORAGE_KEY) || "";
    if (UUID_PATTERN.test(existing)) return existing;
  } catch {
    // A private browser can disable session storage. The in-memory ID returned
    // below still keeps retries stable for this mounted form.
  }

  const submissionId = createId();
  if (!UUID_PATTERN.test(submissionId)) {
    throw new Error("Invalid access request submission ID");
  }
  try {
    storage.setItem(ACCESS_REQUEST_SUBMISSION_STORAGE_KEY, submissionId);
  } catch {
    // The caller retains this ID in React state when storage is unavailable.
  }
  return submissionId;
}

export function clearAccessRequestSubmissionId(storage: Storage): void {
  try {
    storage.removeItem(ACCESS_REQUEST_SUBMISSION_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in hardened browser modes.
  }
}

export function buildAccessRequestPayload(
  intake: AccessRequestIntake,
  submissionId: string,
): AccessRequestPayload {
  if (!UUID_PATTERN.test(submissionId)) {
    throw new Error("Invalid access request submission ID");
  }
  const phone = intake.companyPhone.replace(/\D/g, "");
  const brazilianCompany = /^(br|bra|brasil|brazil)$/i.test(intake.country.trim());
  const nationalPhone = brazilianCompany && (phone.length === 10 || phone.length === 11);
  return {
    submissionId,
    companyName: intake.companyName.trim(),
    cnpj: intake.cnpj.replace(/\D/g, ""),
    email: intake.companyEmail.trim().toLowerCase(),
    phone,
    phoneCountry: nationalPhone ? "+55" : "",
    phoneArea: nationalPhone ? phone.slice(0, 2) : "",
    country: intake.country.trim(),
    state: intake.state.trim(),
    city: intake.city.trim(),
    zipCode: intake.zipCode.replace(/\D/g, ""),
    street: intake.street.trim(),
    numberStreet: intake.numberStreet.trim(),
    neighborhood: intake.neighborhood.trim(),
    applicantName: intake.applicantName.trim(),
    applicantEmail: intake.applicantEmail.trim().toLowerCase(),
    applicantPhone: intake.applicantPhone.replace(/\D/g, ""),
  };
}

export function readVerificationToken(hash: string): string {
  const raw = hash.replace(/^#/, "");
  if (!raw) return "";
  try {
    return decodeURIComponent(raw);
  } catch {
    return "";
  }
}

export function resolveVerificationToken(hash: string, storage: Storage): string {
  const direct = readVerificationToken(hash);
  if (direct) {
    try {
      storage.setItem(VERIFICATION_TOKEN_STORAGE_KEY, direct);
    } catch {
      // The in-memory value still works for this page load when storage is disabled.
    }
    return direct;
  }
  try {
    return storage.getItem(VERIFICATION_TOKEN_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function clearVerificationToken(storage: Storage): void {
  try {
    storage.removeItem(VERIFICATION_TOKEN_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in hardened browser modes.
  }
}

export function cleanVerificationLocation(location: Location, history: History): void {
  const url = new URL(location.href);
  url.hash = "";
  if (url.searchParams.has("verificationSessionId")) {
    // Provider callbacks may add fields over time. None are authorization
    // inputs, so clear the complete callback query instead of leaving an
    // unexpected session/detail parameter in history or downstream referrers.
    url.search = "";
  } else {
    url.searchParams.delete("status");
  }
  history.replaceState(history.state, "", `${url.pathname}${url.search}`);
}

export function readOwnerInviteToken(hash: string, search: string): string {
  const fragmentToken = readVerificationToken(hash);
  if (fragmentToken) return fragmentToken;
  return new URLSearchParams(search).get("token") || "";
}

export function cleanOwnerInviteLocation(location: Location, history: History): void {
  const url = new URL(location.href);
  url.hash = "";
  url.searchParams.delete("token");
  history.replaceState(history.state, "", `${url.pathname}${url.search}`);
}

export function parseVerificationSession(payload: unknown): VerificationSessionView {
  if (!payload || typeof payload !== "object") {
    throw new Error("Invalid verification response");
  }

  const data = payload as Record<string, unknown>;
  if (!isVerificationStatus(data.verificationStatus)) {
    throw new Error("Invalid verification response: verificationStatus");
  }
  if (!isAuthorityStatus(data.authorityStatus)) {
    throw new Error("Invalid verification response: authorityStatus");
  }
  if (!isCompanyVerificationStatus(data.companyVerificationStatus)) {
    throw new Error("Invalid verification response: companyVerificationStatus");
  }
  if (!isIdentityVerificationStatus(data.identityVerificationStatus)) {
    throw new Error("Invalid verification response: identityVerificationStatus");
  }
  if (!isAuthorityEvidenceStatus(data.authorityEvidenceStatus)) {
    throw new Error("Invalid verification response: authorityEvidenceStatus");
  }
  if (!isReviewScope(data.reviewScope)) {
    throw new Error("Invalid verification response: reviewScope");
  }
  if (!isVerificationOutcomeClass(data.outcomeClass)) {
    throw new Error("Invalid verification response: outcomeClass");
  }
  if (typeof data.outcomeSettled !== "boolean") {
    throw new Error("Invalid verification response: outcomeSettled");
  }
  const settlementDeadlineAt = optionalString(data.settlementDeadlineAt);
  if (
    !data.outcomeSettled &&
    (!settlementDeadlineAt || Number.isNaN(Date.parse(settlementDeadlineAt)))
  ) {
    throw new Error("Invalid verification response: settlementDeadlineAt");
  }
  if (data.outcomeSettled && settlementDeadlineAt) {
    throw new Error("Invalid verification response: settled outcome deadline");
  }
  const terminalNonApproval =
    data.outcomeClass === "hard_declined" || data.outcomeClass === "retryable_not_approved";
  if (
    (data.outcomeClass === "approved" && data.verificationStatus !== "approved") ||
    (data.outcomeClass === "pending" && data.outcomeSettled) ||
    (terminalNonApproval &&
      data.verificationStatus !== "declined" &&
      data.verificationStatus !== "expired") ||
    (data.outcomeClass === "pending" &&
      (data.verificationStatus === "declined" || data.verificationStatus === "expired"))
  ) {
    throw new Error("Invalid verification response: inconsistent outcomeClass");
  }
  if (typeof data.activationClaimed !== "boolean") {
    throw new Error("Invalid verification response: activationClaimed");
  }

  const rawConsent = data.consentPolicy;
  if (!rawConsent || typeof rawConsent !== "object") {
    throw new Error("Invalid verification response: consentPolicy");
  }
  const consent = rawConsent as Record<string, unknown>;

  let activation: OwnerActivationSummary | undefined;
  if (data.activation && typeof data.activation === "object") {
    const rawActivation = data.activation as Record<string, unknown>;
    activation = {
      orgId: requiredString(rawActivation.orgId, "activation.orgId"),
      inviteId: requiredString(rawActivation.inviteId, "activation.inviteId"),
    };
  }
  const providerStatus = optionalString(data.providerStatus) || "";
  const approvedHardGates =
    data.verificationStatus === "approved" &&
    data.authorityStatus === "verified" &&
    data.companyVerificationStatus === "verified" &&
    data.identityVerificationStatus === "verified" &&
    (data.authorityEvidenceStatus === "verified" ||
      data.authorityEvidenceStatus === "not_required") &&
    data.reviewScope === "none" &&
    providerStatus.toUpperCase() === "APPROVED";
  if (data.outcomeClass === "approved" && !approvedHardGates) {
    throw new Error("Invalid verification response: incomplete approved outcome");
  }
  if (data.outcomeClass === "approved" && data.outcomeSettled && !activation) {
    throw new Error("Invalid verification response: settled approval activation");
  }

  return {
    verificationId: requiredString(data.verificationId, "verificationId"),
    accessRequestId: requiredString(data.accessRequestId, "accessRequestId"),
    verificationStatus: data.verificationStatus,
    authorityStatus: data.authorityStatus,
    companyVerificationStatus: data.companyVerificationStatus,
    identityVerificationStatus: data.identityVerificationStatus,
    authorityEvidenceStatus: data.authorityEvidenceStatus,
    reviewScope: data.reviewScope,
    outcomeClass: data.outcomeClass,
    outcomeSettled: data.outcomeSettled,
    settlementDeadlineAt,
    provider: requiredString(data.provider, "provider"),
    providerStatus,
    providerUrl: optionalString(data.providerUrl),
    reasonCode: optionalString(data.reasonCode),
    expiresAt: requiredString(data.expiresAt, "expiresAt"),
    activationClaimed: data.activationClaimed,
    activation,
    consentPolicy: {
      version: requiredString(consent.version, "consentPolicy.version"),
      notice: requiredString(consent.notice, "consentPolicy.notice"),
      provider: requiredString(consent.provider, "consentPolicy.provider"),
      daKasaPrivacyUrl: requiredString(consent.daKasaPrivacyUrl, "consentPolicy.daKasaPrivacyUrl"),
      providerPrivacyUrl: requiredString(
        consent.providerPrivacyUrl,
        "consentPolicy.providerPrivacyUrl",
      ),
      providerTermsUrl: requiredString(consent.providerTermsUrl, "consentPolicy.providerTermsUrl"),
      providerRetentionDays: nonNegativeInteger(
        consent.providerRetentionDays,
        "consentPolicy.providerRetentionDays",
      ),
    },
  };
}

export function isFinalApprovedVerification(session?: VerificationSessionView | null): boolean {
  return Boolean(
    session &&
    session.verificationStatus === "approved" &&
    session.outcomeClass === "approved" &&
    session.outcomeSettled &&
    session.authorityStatus === "verified" &&
    session.companyVerificationStatus === "verified" &&
    session.identityVerificationStatus === "verified" &&
    (session.authorityEvidenceStatus === "verified" ||
      session.authorityEvidenceStatus === "not_required") &&
    session.reviewScope === "none" &&
    session.providerStatus.trim().toUpperCase() === "APPROVED" &&
    Boolean(session.activation),
  );
}

function verificationHeaders(token: string) {
  return { "X-Verification-Token": token };
}

export async function submitAccessRequest(
  intake: AccessRequestIntake,
  submissionId: string,
): Promise<AccessRequestSubmissionResponse> {
  const response = await verificationAPI.post<AccessRequestSubmissionResponse>(
    "/access-request",
    buildAccessRequestPayload(intake, submissionId),
  );
  return response.data;
}

export async function getVerificationSession(
  token: string,
  signal?: AbortSignal,
): Promise<VerificationSessionView> {
  const response = await verificationAPI.get("/access-request/verification-sessions/status", {
    headers: verificationHeaders(token),
    signal,
  });
  return parseVerificationSession(response.data);
}

export async function startVerificationSession(
  token: string,
  consentPolicyVersion: string,
  signal?: AbortSignal,
): Promise<VerificationSessionView> {
  const response = await verificationAPI.post(
    "/access-request/verification-sessions/start",
    {
      consentAccepted: true,
      consentPolicyVersion,
    },
    { headers: verificationHeaders(token), signal },
  );
  return parseVerificationSession(response.data);
}

export async function activateVerifiedOwner(
  token: string,
  signal?: AbortSignal,
): Promise<OwnerActivation> {
  const response = await verificationAPI.post(
    "/access-request/verification-sessions/activation",
    undefined,
    { headers: verificationHeaders(token), signal },
  );
  const data = response.data as Partial<OwnerActivation> | undefined;
  return {
    orgId: requiredString(data?.orgId, "activation.orgId"),
    inviteId: requiredString(data?.inviteId, "activation.inviteId"),
    inviteToken: requiredString(data?.inviteToken, "activation.inviteToken"),
    inviteUrl: requiredString(data?.inviteUrl, "activation.inviteUrl"),
  };
}

export function ownerInvitePath(activation: OwnerActivation): string {
  return `/invite/${encodeURIComponent(activation.inviteId)}#${encodeURIComponent(
    activation.inviteToken,
  )}`;
}

export function providerNavigationUrl(rawUrl: string, currentOrigin: string): string {
  const target = new URL(rawUrl);
  const current = new URL(currentOrigin);
  const localHosts = ["localhost", "127.0.0.1"];
  const isLocalDevelopment =
    localHosts.includes(current.hostname) &&
    localHosts.includes(target.hostname) &&
    (target.protocol === "http:" || target.protocol === "https:");
  const runtimeHosts = (window as any).__RUNTIME_CONFIG__?.REACT_APP_VERIFICATION_PROVIDER_HOSTS;
  const configuredHosts = String(
    runtimeHosts || process.env.REACT_APP_VERIFICATION_PROVIDER_HOSTS || "verify.didit.me",
  )
    .split(",")
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);
  const trustedProvider =
    target.protocol === "https:" &&
    (target.port === "" || target.port === "443") &&
    configuredHosts.includes(target.hostname.toLowerCase());
  if (
    target.username ||
    target.password ||
    target.hash ||
    (!isLocalDevelopment && !trustedProvider)
  ) {
    throw new Error("Invalid provider URL");
  }
  return target.toString();
}
